import React, { useState, useRef } from 'react';
import { MediaFileUploadResponse } from '../types/api';
import { apiService } from '../services/api';
import { Upload, X, File, Image, Film } from 'lucide-react';

interface MediaUploadProps {
  onUploadComplete: (uploadedFile: MediaFileUploadResponse) => void;
  onCancel?: () => void;
}

export const MediaUpload: React.FC<MediaUploadProps> = ({ onUploadComplete, onCancel }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [notes, setNotes] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const supportedTypes = [
    // Images
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    // Videos (aligned with backend)
    'video/mp4',
    'video/quicktime', // MOV
    'video/x-msvideo', // AVI
    'video/x-matroska', // MKV
    'video/webm',
  ];

  // Backend limit is 50MB
  const maxFileSize = 50 * 1024 * 1024;

  const validateFile = (file: File): string | null => {
    if (!supportedTypes.includes(file.type)) {
      return 'File type not supported. Please upload JPEG, PNG, GIF, WebP images or MP4, MOV (QuickTime), AVI, MKV, WebM videos.';
    }
    
    if (file.size > maxFileSize) {
      return 'File size too large. Maximum file size is 50MB.';
    }
    
    return null;
  };

  const handleFileSelect = (file: File) => {
    const error = validateFile(file);
    if (error) {
      alert(error);
      return;
    }
    setSelectedFile(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress for better UX (since we don't have real progress from fetch)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 100);

      const response = await apiService.uploadMediaFile(selectedFile, notes || undefined);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Show completed state briefly
      setTimeout(() => {
        onUploadComplete(response);
        resetForm();
      }, 500);
      
    } catch (error) {
      setUploadProgress(0);
      console.error('Upload failed:', error);
      const message = error instanceof Error ? error.message : 'Upload failed. Please try again.';
      alert(message);
    } finally {
      setIsUploading(false);
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setNotes('');
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return <Image size={24} className="text-green-600" />;
    if (file.type.startsWith('video/')) return <Film size={24} className="text-blue-600" />;
    return <File size={24} className="text-gray-600" />;
  };

  const FilePreview = () => {
    if (!selectedFile) return null;

    return (
      <div className="border border-gray-200 rounded-lg p-4 mb-4">
        <div className="flex items-center space-x-3">
          {getFileIcon(selectedFile)}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{selectedFile.name}</p>
            <p className="text-sm text-gray-500">{formatFileSize(selectedFile.size)}</p>
          </div>
          <button
            onClick={resetForm}
            disabled={isUploading}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>
        
        {selectedFile.type.startsWith('image/') && (
          <div className="mt-3">
            <img
              src={URL.createObjectURL(selectedFile)}
              alt="Preview"
              className="w-full h-32 object-cover rounded"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Media</h3>
      
      {!selectedFile ? (
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            dragActive 
              ? 'border-blue-400 bg-blue-50' 
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <Upload size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-lg font-medium text-gray-900 mb-2">
            Drop your files here, or click to browse
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Supports images (JPEG, PNG, GIF, WebP) and videos (MP4, MOV, AVI, MKV, WebM)
          </p>
          <p className="text-xs text-gray-400">Maximum file size: 100MB</p>
          
          <input
            ref={fileInputRef}
            type="file"
            accept={supportedTypes.join(',')}
            onChange={handleFileInputChange}
            className="hidden"
          />
        </div>
      ) : (
        <>
          <FilePreview />
          
          <div className="mb-4">
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
              Notes (optional)
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add a description or notes about this media..."
              className="w-full p-3 border border-gray-300 rounded-md text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
              maxLength={1000}
              disabled={isUploading}
            />
            <p className="text-xs text-gray-500 mt-1">{notes.length}/1000 characters</p>
          </div>

          {isUploading && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Uploading...</span>
                <span className="text-sm text-gray-500">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}

          <div className="flex items-center space-x-3">
            <button
              onClick={handleUpload}
              disabled={isUploading}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isUploading ? 'Uploading...' : 'Upload'}
            </button>
            
            {onCancel && (
              <button
                onClick={onCancel}
                disabled={isUploading}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
              >
                Cancel
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};
